import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Organisation } from '../models/organisation';
import { UserDataService } from '../services/userDataService';
import { SystemAdminDataService } from '../services/systemAdminDataService';
import {
  SupportDataService, SupportOrganisation, SupportSessionService, formatSupportExpiry,
  supportOrgToOrganisation, apiErrorMessage,
} from '../services/supportDataService';
import { useAppState } from '../context/AppStateContext';
import rustleLogo from '../images/rustle_it_logo.png';
import { setSelectedOrgId, clearSelectedOrgId } from '../services/selectedOrg';
import PickerSignOutButton from '../components/common/PickerSignOutButton';
import RequestSupportAccessModal from '../components/RequestSupportAccessModal';

/* ── Optical logo sizing ───────────────────────────────────────────────
   Org logos arrive at wildly different aspect ratios — a square badge, a
   long wordmark, an upright crest. Fitting them all to one box makes the
   wordmark read as a fraction of the badge's size, because "contain" only
   equalises the bounding box, not the ink on screen.

   So scale by area instead: hold width × height roughly constant, which
   is the usual trick for balancing mixed artwork. A square logo lands at
   LOGO_OPTICAL_SIZE²; wider logos get proportionally wider and shorter
   for the same area. The clamps stop a very wide or very tall logo from
   outgrowing the tile. */
const LOGO_OPTICAL_SIZE = 76; // px — the side a 1:1 logo renders at
const LOGO_MAX_W = 150;       // keeps clear of the card's 0.85rem padding
const LOGO_MAX_H = 84;        // the logo row's height, so tiles stay aligned

const fitLogo = (naturalW: number, naturalH: number) => {
  const ratio = naturalW / naturalH;
  const w = LOGO_OPTICAL_SIZE * Math.sqrt(ratio);
  const h = LOGO_OPTICAL_SIZE / Math.sqrt(ratio);
  const scale = Math.min(1, LOGO_MAX_W / w, LOGO_MAX_H / h);
  return { width: w * scale, height: h * scale };
};

const OrgLogo: React.FC<{ src: string }> = ({ src }) => {
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);

  // Held back until measured, otherwise the logo paints at its contain-fit
  // size and then snaps to the optical size — most visible on wide logos.
  const measure = (img: HTMLImageElement | null) => {
    if (!img?.naturalWidth || !img.naturalHeight) return;
    // Returning the previous object when already measured lets React bail
    // out — the ref callback re-runs on every render.
    setSize(prev => prev ?? fitLogo(img.naturalWidth, img.naturalHeight));
  };

  return (
    <img
      src={src}
      alt=""
      // A cached image can already be complete before onLoad would fire,
      // so measure from the ref as well.
      ref={img => { if (img?.complete) measure(img); }}
      onLoad={e => measure(e.currentTarget)}
      style={size ? { ...size, opacity: 1 } : { opacity: 0 }}
    />
  );
};

const OrgSelect: React.FC = () => {
  const navigate = useNavigate();
  const { setSelectedOrganisation, setActiveUser, setActiveUserToken } = useAppState();
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [isSupport, setIsSupport] = useState(false);
  const [supportOrganisations, setSupportOrganisations] = useState<SupportOrganisation[]>([]);
  const [showRequest, setShowRequest] = useState(false);
  // Set by SupportSessionBanner when a session ended because the grant did.
  const [supportNotice, setSupportNotice] = useState<string | null>(() => {
    const ended = sessionStorage.getItem('supportSessionEnded');
    if (ended) sessionStorage.removeItem('supportSessionEnded');
    return ended;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [enteringSupport, setEnteringSupport] = useState(false);

  /**
   * Enter an organisation as support. No PIN step: support is not staff. The
   * API checks the grant again here and on every request after, so a grant
   * that ended since the picker loaded is refused rather than half-entered.
   */
  const enterSupport = async (org: SupportOrganisation) => {
    setEnteringSupport(true);
    setSupportNotice(null);
    try {
      const session = await SupportSessionService.start(org.id);
      setSelectedOrgId(org.id);
      setSelectedOrganisation(supportOrgToOrganisation(org));
      sessionStorage.setItem('activeUserToken', session.activeUserToken);
      setActiveUserToken(session.activeUserToken);
      setActiveUser(session.activeUser);
      // Organisation settings: readable in a support session from the start,
      // unlike the Dashboard, which is built from posts.
      navigate('/admin?tab=organisation');
    } catch (err) {
      setSupportNotice(apiErrorMessage(err, 'Could not open a support session.'));
      setEnteringSupport(false);
    }
  };

  const selectOrg = async (org: Organisation) => {
    setSelectedOrgId(org.id);
    setSelectedOrganisation(org);
    try {
      const result = await UserDataService.loginAsSelf();
      sessionStorage.setItem('activeUserToken', result.activeUserToken);
      setActiveUserToken(result.activeUserToken);
      setActiveUser(result.activeUser);
    } catch {
      // If auto-login fails (e.g. user not found in org), fall back to staff select
      navigate('/staff-select');
      return;
    }
    navigate('/');
  };

  useEffect(() => {
    // Clear any previously stored org so the user always makes a fresh choice.
    // Also clear any stale active-user token from a previous session — leaving
    // it in place would let IdleLockGuard hit /security/organisation with a
    // mismatched org/user pair after we set the new selectedOrgId.
    clearSelectedOrgId();
    sessionStorage.removeItem('activeUserToken');
    setActiveUser(null);
    setActiveUserToken(null);

    const load = async () => {
      try {
        // Resolve workspaces and system-admin status together. The admin check
        // never rejects for a plain user (returns false); a failure there must
        // not block the workspace picker, so it's caught independently.
        // Support staff are members of no organisation, so the workspace list
        // is empty for them (404 when the account was never invited anywhere)
        // and the support list is what they pick from. A failed support lookup
        // must not block an ordinary user's picker; it degrades to "not support".
        const [orgs, admin, support] = await Promise.all([
          UserDataService.fetchOrganisations().catch(err =>
            err?.response?.status === 404 ? [] : Promise.reject(err)),
          SystemAdminDataService.checkSystemAdmin().catch(() => false),
          SupportDataService.fetchMyOrganisations().catch(() => ({ isSupport: false, organisations: [] })),
        ]);
        setIsSystemAdmin(admin);
        setIsSupport(support.isSupport);
        setSupportOrganisations(support.organisations);

        if (orgs.length === 0 && !admin && !support.isSupport) {
          setError('You do not belong to any organisation. Please contact your administrator.');
          setLoading(false);
          return;
        }
        // Only auto-skip the picker for a plain user with exactly one workspace.
        // A system admin or support user always sees the choice.
        if (orgs.length === 1 && !admin && !support.isSupport) {
          await selectOrg(orgs[0]);
          return;
        }
        setOrganisations(orgs);
        setLoading(false);
      } catch {
        setError('Failed to load your organisations. Please try again.');
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="rustle-background d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div style={{ position: 'relative', zIndex: 1 }} className="d-flex flex-column align-items-center">
          <div className="loading-spinner mb-3" />
          <p className="text-muted">Loading your workspaces…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rustle-background d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <PickerSignOutButton />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div className="alert alert-danger" style={{ maxWidth: 420 }}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="rustle-background" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <PickerSignOutButton />
      <div className="picker-shell">
        <img src={rustleLogo} alt="Rustle" style={{ maxWidth: 220, marginBottom: '2.5rem' }} />

        {/* One line, and a label rather than a title — see .picker-heading.
            The old sub-line explained the System Admin tile, which the tile
            already says on its own face; "where to work" covers a workspace
            and the dashboard alike, so the copy no longer forks. */}
        <div className="picker-heading">
          <h1 className="picker-heading__title">Choose where to work</h1>
        </div>

        {/* Same tile language as the Admin Child Profiles / Users grids. */}
        <div className="picker-grid">
          {isSystemAdmin && (
            <button
              key="system-admin"
              type="button"
              onClick={() => navigate('/system-admin')}
              className="child-card picker-card org-card org-card--admin"
              title="Open the System Admin dashboard"
            >
              <div className="org-card__logo">
                <span className="org-card__monogram" aria-hidden="true">
                  <i className="fas fa-shield-halved" />
                </span>
              </div>
              <div className="child-card__body">
                <div className="child-card__name">System Admin</div>
                <div className="child-card__alias" style={{ fontStyle: 'normal' }}>
                  Platform dashboard
                </div>
              </div>
            </button>
          )}

          {organisations.map((org) => {
            const brand = org.brandColour || '#26923f';
            return (
              <button
                key={org.id}
                type="button"
                onClick={() => selectOrg(org)}
                className="child-card picker-card org-card"
                title={`Open ${org.name}`}
              >
                <div className="org-card__logo">
                  {org.logoUrl ? (
                    <OrgLogo src={org.logoUrl} />
                  ) : (
                    <span
                      className="org-card__monogram"
                      style={{ background: brand }}
                      aria-hidden="true"
                    >
                      {org.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="child-card__body">
                  <div className="child-card__name">{org.name}</div>
                </div>
                {/* External only. A role name doesn't change which workspace
                    you pick — you already know where you're going, and the
                    nav tells you what you can do the moment you're inside.
                    "External" does change it: this is a setting you're a
                    guest in, with posts and children filtered to your
                    whitelist. roleNames is also a list, so tiles ended up
                    carrying one, three or no pills depending on an attribute
                    with no bearing on the choice. */}
                {org.isExternal && (
                  <div className="picker-card__tags">
                    <span className="picker-card__tag picker-card__tag--external">External</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Support access. Its own section, under its own label, because
            entering one of these is not working in a workspace: it is a
            time-limited, audited visit the organisation's admin approved. */}
        {isSupport && (
          <>
            <div className={`picker-heading${organisations.length > 0 || isSystemAdmin ? ' picker-heading--section' : ''}`}>
              <h2 className="picker-heading__title">Support access</h2>
            </div>
            {supportOrganisations.length === 0 ? (
              <div className="picker-empty">
                <i className="fas fa-life-ring picker-empty__icon" aria-hidden="true" />
                <p className="picker-empty__title">No organisations have given support access at the moment</p>
                <p className="picker-empty__text">
                  When an organisation's admin approves a support request, it appears here
                  until the access expires.
                </p>
              </div>
            ) : (
              <div className="picker-grid">
                {supportOrganisations.map((org) => (
                  <button
                    key={`support-${org.id}`}
                    type="button"
                    className="child-card picker-card org-card"
                    title={`Open ${org.name} as Rustle It Support`}
                    onClick={() => enterSupport(org)}
                    disabled={enteringSupport}
                  >
                    <div className="org-card__logo">
                      {org.logoUrl ? (
                        <OrgLogo src={org.logoUrl} />
                      ) : (
                        <span className="org-card__monogram" style={{ background: '#26923f' }} aria-hidden="true">
                          {org.name.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="child-card__body">
                      <div className="child-card__name">{org.name}</div>
                    </div>
                    <div className="picker-card__tags">
                      <span className="picker-card__tag picker-card__tag--support">
                        Until {formatSupportExpiry(org.expiresAt)}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {supportNotice && <div className="picker-notice" role="status">{supportNotice}</div>}
            <button type="button" className="btn btn-outline-primary btn-sm picker-request-btn" onClick={() => setShowRequest(true)}>
              <i className="fas fa-paper-plane me-2" aria-hidden="true" />
              Request access to an organisation
            </button>
          </>
        )}
      </div>
      <RequestSupportAccessModal
        show={showRequest}
        onClose={() => setShowRequest(false)}
        onRequested={(message) => { setShowRequest(false); setSupportNotice(message); }}
      />
    </div>
  );
};

export default OrgSelect;
